import {
  Box,
  Divider,
  Flex,
  Price,
  PrimaryButton,
  Spinner,
  Stack,
  Text,
} from "flicket-ui";
import isEmpty from "lodash/isEmpty";
import { ReactNode } from "react";
import { ContentModal } from "~components";
import { FixedRatioImage, Icon, SlateRenderer } from "~components/common";
import { getAfterPayLocaleByCurrency } from "~config";
import { BNPLIntegration } from "~features/checkout/checkout.types";
import BuyNowPayLater from "~features/seated-reservation/components/common/common.BuyNowPayLater";
import {
  MembershipsSoldForUserQuery,
  ReferralRewardType,
  ReleaseType,
  TicketsSoldForUserQuery,
} from "~graphql/sdk";
import { isVisible } from "./non-seated.helpers";
import { ListItem } from "./non-seated.ListItem";
import { ImageGalleryItem } from "../../../graphql/typed-document-nodes";
import {
  IReleaseTicket,
  IReleaseZone,
} from "~features/nonseated-reservation/nonseated-reservation.types";
import {
  INonSeatedProps,
  TSoldOutWaitlistMessage,
  useViewModel,
} from "~features/nonseated-reservation/components/NonSeatedList/NonSeatedList.useViewModel";
import { DetailsModal } from "~features/nonseated-reservation/components/DetailsModal/DetailsModal";
import { useRouter } from "next/router";
import { addQueryParams, removeQueryParams } from "~lib/helpers/queryParams";
import RichTextPreview from "~features/nonseated-reservation/components/common/RichTextPreview/RichTextPreview";
import { hasRichTextContent } from "~lib/helpers/hasRichTextContent";

const hasImportantNotice = (importantNotice?: string) =>
  importantNotice &&
  (JSON.parse(importantNotice) as any[]).some(({ children }) =>
    children.some(({ text }) => !isEmpty(text))
  );

export const ReservationList = (props: INonSeatedProps) => {
  const {
    data,
    zones,
    isExperienceSales,
    delayedIsSubmitting,
    importantNotice,
    showIsR18,
    ticketTypeVariantTitle,
    hasResaleWaitlist,
    showHiddenFees,
    isAdmin,
    selectedTickets,
    selectedAddons,
    releaseType,
    requiresAuth,
    ticketsSoldForUser,
    SoldOutWaitlistMessage,
    showWaitlistMessage,
    addonTypes,
    multibuyDiscount,
    lineItemsTotal,
    activeReferralCampaign,
    hasTicketSelected,
    userCredits,
    user,
    totalPrice,
    isPOS,
    bnplGateways,
    stripeAfterpayGateway,
    isSubmitting,
    primaryButtonLabel,
    alertError,
    setAlertError,
    orgCurrency,
    isMobile,
    referralDiscount,
    events: { onChangeQuantity, onOrderCreationClick, onClickZone },
  } = useViewModel(props);

  if (delayedIsSubmitting) {
    return <DelayedSubmissionMessage />;
  }

  return (
    <Box
      maxW={{ _: "100%", md: isExperienceSales ? "920px" : "640px" }}
      m={{ _: 0, md: "0 auto" as any }}
      px={isExperienceSales ? 0 : 2}
    >
      {hasImportantNotice(importantNotice) && !isPOS && (
        <Box mb={showIsR18 ? 2 : 5}>
          <ImportantNotice value={importantNotice} />
        </Box>
      )}

      {showIsR18 && <R18Notice />}

      <Section
        isExperienceSales={isExperienceSales}
        title={
          isExperienceSales ? undefined : `Select ${ticketTypeVariantTitle}`
        }
      >
        <ZoneList
          isExperienceSales={isExperienceSales}
          zones={zones}
          hasResaleWaitlist={hasResaleWaitlist}
          showHiddenFees={showHiddenFees}
          handleChange={onChangeQuantity}
          isAdmin={isAdmin}
          selectedTickets={selectedTickets}
          releaseType={releaseType}
          requiresAuth={requiresAuth}
          ticketsSoldForUser={ticketsSoldForUser}
          SoldOutWaitlistMessage={SoldOutWaitlistMessage}
          showWaitlistMessage={showWaitlistMessage}
          onClickZone={onClickZone}
          eventId={data.type === "event" ? data.id : undefined}
        />
      </Section>

      {addonTypes.length > 0 && (
        <Section
          id="add-ons"
          isExperienceSales={isExperienceSales}
          title={isExperienceSales ? "Add-ons" : "Select Addons"}
        >
          {!isExperienceSales && <Divider mb={{ _: 2, md: 1 }} />}

          {addonTypes.map((addonType, idx) => {
            const selectedQuantity = selectedAddons?.find(
              (ao) => ao.id === addonType.id
            );

            return (
              <ListItem
                key={addonType.id}
                ticketType={addonType}
                handleChange={onChangeQuantity}
                quantity={selectedQuantity?.quantity}
                isFirstItem={idx === 0}
                isLastItem={idx === addonTypes.length}
              />
            );
          })}
        </Section>
      )}

      {!isExperienceSales && (
        <>
          <Divider mt={{ _: 3, md: 0 }} />

          {multibuyDiscount > 0 && (
            <>
              <Flex mt={2} justifyContent="space-between" w="100%">
                <Text fontWeight="medium" fontSize={2} color="N600">
                  Sub-Total
                </Text>
                <Price
                  price={lineItemsTotal}
                  fontWeight="medium"
                  fontSize={4}
                  lineHeight={"18px" as any}
                  color={"black" as any}
                />
              </Flex>
              <Flex mt={2} justifyContent="space-between" w="100%">
                <Text fontWeight="medium" fontSize={2} color="N600">
                  Multibuy promotion applied
                </Text>
                <Price
                  price={-multibuyDiscount}
                  fontWeight="medium"
                  fontSize={4}
                  lineHeight={"18px" as any}
                  color={"black" as any}
                />
              </Flex>
              <Divider my={{ _: 3, md: 2 }} />
            </>
          )}

          {activeReferralCampaign &&
            activeReferralCampaign.referralUserRewardType ===
              ReferralRewardType.Discount && (
              <>
                <Flex mt={2} justifyContent="space-between" w="100%">
                  <Text fontWeight="medium" fontSize={2} color="N600">
                    Sub-Total
                  </Text>
                  <Price
                    price={lineItemsTotal}
                    fontWeight="medium"
                    fontSize={4}
                    lineHeight={"18px" as any}
                    color={"black" as any}
                  />
                </Flex>
                <Flex mt={2} justifyContent="space-between" w="100%">
                  <Text fontWeight="medium" fontSize={2} color="N600">
                    Referral discount applied
                  </Text>
                  <Price
                    price={-referralDiscount}
                    fontWeight="medium"
                    fontSize={4}
                    lineHeight={"18px" as any}
                    color={"black" as any}
                  />
                </Flex>
                <Divider my={{ _: 3, md: 2 }} />
              </>
            )}

          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Total {showHiddenFees ? "(including booking fees and taxes)" : ""}
            </Text>
            <Text
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {hasTicketSelected ? <Price price={totalPrice} /> : "-"}
            </Text>
          </Flex>

          {userCredits > 0 && hasTicketSelected && (
            <>
              <Flex mt={2} justifyContent="space-between" w="100%">
                <Text fontWeight="medium" fontSize={2} color="N600">
                  Your credits to be deducted
                </Text>
                <Text
                  fontWeight="medium"
                  fontSize={4}
                  lineHeight={"18px" as any}
                  color={"black" as any}
                >
                  {`- $${user.credits.toFixed(2)}`}
                </Text>
              </Flex>
              <Flex mt={2} justifyContent="space-between" w="100%">
                <Text fontWeight="medium" fontSize={2} color="N600">
                  Total left to pay after deducting credits
                </Text>
                <Text
                  fontWeight="medium"
                  fontSize={4}
                  lineHeight={"18px" as any}
                  color={"black" as any}
                >
                  {totalPrice - userCredits > 0 ? (
                    <Price price={totalPrice - userCredits} />
                  ) : (
                    "$0.00"
                  )}
                </Text>
              </Flex>
            </>
          )}

          {!isPOS && totalPrice - userCredits > 0 && (
            <>
              {bnplGateways.length > 0 &&
                bnplGateways.map((g) => (
                  <Box mt={2} w="100%" key={g.id}>
                    <BuyNowPayLater
                      totalPrice={totalPrice - userCredits}
                      hasTicketSelected={hasTicketSelected}
                      showHiddenFees={showHiddenFees}
                      isSmallDisplay={isMobile}
                      bnplIntegration={g as BNPLIntegration} // This was checked when filtering.
                    />
                  </Box>
                ))}
              {stripeAfterpayGateway && (
                <Flex flexDirection={"row-reverse"}>
                  {/* @ts-expect-error - Afterpay placement component */}
                  <afterpay-placement
                    data-locale={getAfterPayLocaleByCurrency(orgCurrency)}
                    data-currency={orgCurrency ?? "NZD"}
                    data-amount={totalPrice - userCredits}
                  >
                    {/* @ts-expect-error - Afterpay placement component */}
                  </afterpay-placement>
                </Flex>
              )}
            </>
          )}

          <PrimaryButton
            mt={{ _: 2, md: 6 }}
            padding="mobileGutter"
            w="100%"
            onClick={onOrderCreationClick}
            isLoading={isSubmitting}
            disabled={hasTicketSelected === false}
          >
            {primaryButtonLabel}
          </PrimaryButton>
        </>
      )}
      {/* DON"T REMOVE: 
       When youre on safari the buy tickets button is hidden as the nav bar is at the bottom. 
       We might be able to add bottom padding or use dvh css property */}
      <br />
      <br />
      <br />
      <br />
      <br />

      <ContentModal
        isOpen={!!alertError}
        setIsOpen={() => setAlertError(undefined)}
        title={alertError?.title}
      >
        {typeof alertError?.message === "string" ? (
          <div dangerouslySetInnerHTML={{ __html: alertError?.message }} />
        ) : (
          alertError?.message
        )}
      </ContentModal>
    </Box>
  );
};

function Section({
  id,
  isExperienceSales,
  title,
  children,
}: {
  id?: string;
  isExperienceSales: boolean;
  title: ReactNode;
  children?: ReactNode;
}) {
  const TitleComponent = (() => {
    if (isExperienceSales) {
      return <Text variant="header.M">{title}</Text>;
    }

    return (
      <Text
        color="N800"
        fontWeight="heavy"
        fontSize={{ _: 6, md: 7 }}
        textAlign={{ _: "center", md: "center" }}
        lineHeight={{ _: "normal", md: "high" }}
        marginTop={{ _: 3, md: 0 }}
      >
        {title}
      </Text>
    );
  })();

  return (
    <Box id={id}>
      <Stack gap={isExperienceSales ? 0 : 2} direction="vertical" mb={3}>
        {title && TitleComponent}
        {children}
      </Stack>
    </Box>
  );
}

function Group({
  id,
  isExperienceSales,
  title,
  description,
  imageGallery,
  onClick,
  children,
  hasTicketTypes,
}: {
  id: string;
  isExperienceSales: boolean;
  title: string;
  description?: string;
  onClick?: () => void;
  children?: ReactNode;
  imageGallery: ImageGalleryItem[];
  hasTicketTypes: boolean;
}) {
  const router = useRouter();

  const TitleComponent = (() => {
    if (isExperienceSales) {
      const canClick =
        imageGallery?.length > 0 || hasRichTextContent(description);
      return (
        <Box
          cursor={canClick ? "pointer" : "auto"}
          onClick={() => {
            if (canClick) {
              addQueryParams(router, {
                modalId: id,
              });
            }
          }}
          mb={4}
        >
          <Stack
            gap={3}
            flexDirection={{ _: "column-reverse", sm: "row" }}
            justifyContent="space-between"
            flex={1}
          >
            <Box>
              <Text variant="header.M">{title}</Text>
              <Box maxW={"90%"}>
                <RichTextPreview value={description} />
              </Box>
            </Box>
            {imageGallery?.[0] && (
              <FixedRatioImage
                src={imageGallery?.[0].original.src}
                alt={imageGallery?.[0].title}
                aspectRatio={"5 / 2"}
                minWidth={{ _: "100%", xs: 196, sm: 260 }}
                borderRadius={{ sm: "sm" }}
                ml={{ _: -2, sm: 0 } as any}
                mr={{ _: -2, sm: 0 } as any}
              />
            )}
          </Stack>
        </Box>
      );
    }

    return (
      <Box>
        <Text
          color="N800"
          fontWeight="heavy"
          fontSize={{ _: 6, md: 7 }}
          textAlign={{ _: "left", md: "left" }}
          lineHeight="normal"
        >
          {title}
        </Text>
        {description && (
          <Box
            w="100%"
            p={2}
            bg="N100"
            borderRadius="sm"
            border="1px"
            borderColor="N300"
            mt={2}
          >
            <Text
              color="N800"
              fontSize={3}
              textAlign={{ _: "left", md: "left" }}
              lineHeight="medium"
            >
              {description}
            </Text>
          </Box>
        )}
      </Box>
    );
  })();

  return (
    <Box id={id} mb={isExperienceSales ? 8 : 6}>
      {title && TitleComponent}
      {children}
      <DetailsModal
        isOpen={router?.query?.modalId === id}
        close={() => removeQueryParams(router, ["modalId"])}
        imageGallery={imageGallery}
        aspectRatio={"5 / 2"}
        title={title}
        description={description}
        stickySection={({ isSticky }) => (
          <Text variant="header.M" px={isSticky ? { _: 2, sm: 4 } : 0}>
            {title}
          </Text>
        )}
      />
    </Box>
  );
}

export function ZoneList({
  zones,
  releaseType,
  requiresAuth,
  ticketsSoldForUser,
  selectedTickets,
  showWaitlistMessage,
  isAdmin,
  handleChange,
  showHiddenFees,
  SoldOutWaitlistMessage,
  isExperienceSales,
  onClickZone,
  eventId,
}: {
  handleChange: (value: number, id: string, zoneId: string) => void;
  zones: IReleaseZone[];
  isExperienceSales: boolean;
  isAdmin: boolean;
  requiresAuth: boolean;
  hasResaleWaitlist: boolean;
  selectedTickets: IReleaseTicket[];
  releaseType: ReleaseType;
  showHiddenFees: boolean;
  ticketsSoldForUser:
    | TicketsSoldForUserQuery["ticketsSoldForUser"]
    | MembershipsSoldForUserQuery["membershipsSoldForUser"];
  SoldOutWaitlistMessage: TSoldOutWaitlistMessage;
  showWaitlistMessage: boolean;
  onClickZone: (id: string) => void;
  eventId?: string;
}) {
  // this value has derived from the zone ticketTypes which uses a ResolvedField see backend for more info.
  let soldOutZones = zones?.filter((zone) =>
    zone.ticketTypes
      ?.filter((tt) => tt.isActive)
      ?.every((tt) => tt.isSoldOut ?? false)
  );

  // Hack: BIG SKY PBR 2024 - JULY 18-20 (Outlaw)
  // Show waitlist message if at least one ticket type is sold out
  const isBigSkyHack = eventId === "3c7e9396-c775-441d-b720-8925628c80da";

  if (isBigSkyHack) {
    soldOutZones = zones?.filter((zone) =>
      zone.ticketTypes
        ?.filter((tt) => tt.isActive)
        ?.some((tt) => tt.isSoldOut ?? false)
    );
  }

  const list = zones
    ?.map((zone) => {
      if (!zone.isActive) {
        return null;
      }

      const ticketTypeList = zone.ticketTypes.filter((ticketType) => {
        if (!ticketType.isActive && releaseType !== ReleaseType.Presale) {
          return false;
        }

        if (
          !isVisible(selectedTickets, ticketType, ticketsSoldForUser) &&
          !isAdmin
        ) {
          return false;
        }

        return true;
      });

      const ticketListItems = ticketTypeList.map((ticketType, idx) => {
        const selectedQuantity = selectedTickets?.find(
          (seat) => seat.id === ticketType.id && seat.zoneName === zone?.name
        );

        return (
          <ListItem
            key={ticketType.id}
            ticketType={ticketType}
            showHiddenFees={showHiddenFees}
            handleChange={handleChange}
            quantity={selectedQuantity?.quantity}
            isFirstItem={idx === 0}
            isLastItem={idx === ticketTypeList.length - 1}
          />
        );
      });

      if (zone.description || ticketListItems.length > 0) {
        return (
          <Group
            id={zone.id}
            key={zone.id}
            isExperienceSales={isExperienceSales}
            title={zone.name}
            description={zone.description}
            imageGallery={zone.imageGallery}
            hasTicketTypes={ticketListItems.length > 0}
          >
            <>
              {!isExperienceSales && <Divider my={{ _: 2, md: 2 }} />}

              {ticketListItems?.length > 0 && <Box>{ticketListItems}</Box>}

              {showWaitlistMessage &&
                (() => {
                  const soldOutZone = soldOutZones?.find(
                    (z) => z.id === zone.id
                  );

                  const preventWaitlistJoining =
                    (requiresAuth && releaseType === ReleaseType.Unlisted) ||
                    releaseType === ReleaseType.Pos ||
                    releaseType === ReleaseType.Outlet;

                  if (!soldOutZone || preventWaitlistJoining) return null;

                  let zoneName = soldOutZone.name;

                  if (isBigSkyHack) {
                    zoneName = soldOutZone.ticketTypes
                      .filter((t) => t.isActive && t.isSoldOut)
                      .map((t) => t.name)
                      .join(", ");
                  }

                  return (
                    <>
                      {!isExperienceSales && <Divider my={{ _: 2, md: 2 }} />}
                      <SoldOutWaitlistMessage zoneName={zoneName} />
                    </>
                  );
                })()}
            </>
          </Group>
        );
      }

      return null;
    })
    .filter((v) => v);

  return <>{list}</>;
}

function DelayedSubmissionMessage() {
  return (
    <Box mx={"1/2"} my={20} px={4}>
      <Spinner size={64} />
      <Text
        mt={4}
        fontSize={4}
        lineHeight="high"
        color="N600"
        textAlign="center"
      >
        We&apos;re just double-checking that you&apos;re not a ticket-buying
        robot in disguise.
      </Text>
      <Text
        mt={1}
        fontSize={4}
        lineHeight="high"
        color="N600"
        textAlign="center"
      >
        {" "}
        Your tickets are nearly ready.
      </Text>
      <Text
        textAlign="center"
        mt={4}
        fontSize={4}
        lineHeight="high"
        color="N600"
        fontWeight={"heavy"}
      >
        Don&apos;t refresh this page.
      </Text>
    </Box>
  );
}

function ImportantNotice({ value }: { value: string }) {
  return (
    <Box
      w="100%"
      p={2}
      bg="N100"
      borderRadius="sm"
      border="1px"
      mt={2}
      borderColor="N300"
    >
      <Flex mb={3}>
        <Icon icon="info" color="N600" fontSize={6} />
        <Text
          fontSize={6}
          lineHeight="normal"
          fontWeight="heavy"
          color="N800"
          ml={1}
        >
          Important notes
        </Text>
      </Flex>
      <SlateRenderer value={value} />
    </Box>
  );
}

function R18Notice() {
  return (
    <Flex
      alignItems="center"
      borderRadius="sm"
      backgroundColor="error25"
      px="6/4"
      py={2}
      mb={5}
    >
      <Icon icon="r18" fontSize={6} mr={1} />
      <Text fontWeight="medium" fontSize={2} lineHeight="medium" color="N700">
        This is an R18 event
      </Text>
    </Flex>
  );
}
